/**
 * 根路由入口
 * 运行时根据 UA 和窗口宽度判断平台，懒加载对应路由
 * @module router
 */
import { MiniRoute, PatternBrowserRouter } from '@yimoka/mini-router';
import { ConfigProvider } from '@yimoka/react';
import { lazy, Suspense, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { getPlatform, PLATFORM_H5, setLanguage, SUPPORT_LANGUAGES } from '@/root';

const LazyH5Router = lazy(() => import('./h5').then(m => ({ default: m.H5Router })));
const LazyPcRouter = lazy(() => import('./pc').then(m => ({ default: m.PcRouter })));

/** 从当前 URL 首段解析语言，保证刷新或直达 /zh/*、/en/* 时语言以路径为准 */
const resolveLangFromPath = () => {
  if (typeof window === 'undefined') return undefined;
  const firstSegment = window.location.pathname.split('/').filter(Boolean)[0];
  return SUPPORT_LANGUAGES.find(item => item.path === firstSegment)?.value;
};

/** 根路由组件，根据平台懒加载 H5 或 PC 端路由 */
const PageRouter = () => {
  const platform = getPlatform();
  const Router = platform === PLATFORM_H5 ? LazyH5Router : LazyPcRouter;
  return (
    <ConfigProvider value={{ useNavigate, useRouteParams: useParams, useLocation }}>
      <Suspense fallback={<div style={{ padding: 16, textAlign: 'center' }}>加载中…</div>}>
        <Router />
      </Suspense>
    </ConfigProvider>
  );
};

/** 语言路由壳层，负责从 URL 前缀同步语言并挂载内部业务路由 */
const LangRouter = ({ lang }: { lang?: string }) => {
  const routeLang = lang ?? resolveLangFromPath();

  useEffect(() => {
    routeLang && setLanguage(routeLang);
  }, [routeLang]);

  // 基于首段路径 /zh /en 作为 basename，让内部 React Router 只看到去掉语言前缀的路径
  // 注意：刷新时可能先命中通配路由（lang 为空），但 URL 已带 /zh 或 /en，此时仍需启用 base="/"
  const hasLangPrefix = typeof window !== 'undefined'
    && SUPPORT_LANGUAGES.some(item => window.location.pathname === `/${item.path}`
      || window.location.pathname.startsWith(`/${item.path}/`));
  return (
    <PatternBrowserRouter key={routeLang} base={(routeLang || hasLangPrefix) ? '/' : undefined}>
      <PageRouter />
    </PatternBrowserRouter>
  );
};

export const RootRouter = () => (
  <>
    {SUPPORT_LANGUAGES.map(item => (
      <MiniRoute key={item.path} component={<LangRouter lang={item.value} />} path={`/${item.path}`} />
    ))}
    <MiniRoute component={<LangRouter />} path="*" />
  </>
);
